import { useEffect, useRef, useState } from 'react';

import { Category } from './hgf';

// HGF2 is a simplified successor to the original Human Growth Framework. All
// copy below is transcribed from `HGF 2.0.csv` in the repo root.

export const disciplines = ['developer', 'designer', 'product'] as const;
export type Discipline = typeof disciplines[number];

// Base ("Core Delivery") levels, in order. The reached level alone decides the
// base salary, independent of discipline.
export const hgf2Levels = ['junior', 'mid', 'senior', 'principal'] as const;

// Monthly base salary by reached level (index = reachedCount - 1). Sourced from
// the all-hands deck; the CSV's Junior/Mid/Senior/Principal map by position onto
// the deck's Junior/Intermediate/Senior/Lead salary tiers.
const baseSalary = [3500, 4666.67, 5833.33, 6916.67];
const yearBonusRate = 0.015; // 1.5% of base per year with us
const bonusPerLevel = 0.05; // each management/support level adds 5% of base

export interface HGF2Levels {
  base: number[]; // single track → [reachedCount]
  management: number[]; // [project/customer, people, craft]
  support: number[]; // [evangelism, open source]
}

export interface HGF2Selection {
  discipline: Discipline;
  years: number;
  levels: HGF2Levels;
}

export class HGF2State {
  discipline: Discipline;
  years: number;
  levels: HGF2Levels;
  private onChange: () => void;

  constructor(selection: HGF2Selection, onChange: () => void) {
    this.discipline = selection.discipline;
    this.years = selection.years;
    this.levels = selection.levels;
    this.onChange = onChange;
  }

  private update(): void {
    document.location.hash = this.serialized;
    this.onChange();
  }

  changeRating(section: keyof HGF2Levels, track: number, level: number): void {
    this.levels[section][track] = level;
    this.update();
  }

  setDiscipline(discipline: Discipline): void {
    this.discipline = discipline;
    this.update();
  }

  setYears(years: number): void {
    this.years = years;
    this.update();
  }

  // Applies a selection parsed from the URL hash. Unlike the setters above this
  // does not write the hash back (it is reading *from* it) — it only refreshes
  // the view. Used to restore state on the client after hydration.
  load(selection: HGF2Selection): void {
    this.discipline = selection.discipline;
    this.years = selection.years;
    this.levels = selection.levels;
    this.onChange();
  }

  get base(): number {
    const reached = this.levels.base[0] ?? 0;
    return reached ? baseSalary[reached - 1] : 0;
  }

  get yearBonus(): number {
    return this.base * yearBonusRate * this.years;
  }

  get managementBonus(): number {
    const sum = this.levels.management.reduce((acc, level) => acc + level, 0);
    return this.base * bonusPerLevel * sum;
  }

  get supportBonus(): number {
    const sum = this.levels.support.reduce((acc, level) => acc + level, 0);
    return this.base * bonusPerLevel * sum;
  }

  get total(): number {
    return (
      this.base + this.yearBonus + this.managementBonus + this.supportBonus
    );
  }

  get serialized(): string {
    const job = disciplines.indexOf(this.discipline);
    const base = this.levels.base[0] ?? 0;
    const management = [0, 1, 2]
      .map((i) => this.levels.management[i] ?? 0)
      .join('');
    const support = [0, 1].map((i) => this.levels.support[i] ?? 0).join('');
    return `2j${job}l${base}y${this.years}m${management}s${support}`;
  }
}

function matchInt(value: string, regex: RegExp, fallback: number): number {
  const result = parseInt(value.match(regex)?.[1] ?? '');
  return isNaN(result) ? fallback : result;
}

export function parseHgf2(value: string): HGF2Selection {
  const job = matchInt(value, /j(\d)/, 0);
  const management = value.match(/m(\d)(\d)(\d)/);
  const support = value.match(/s(\d)(\d)/);
  return {
    discipline: disciplines[job] ?? 'developer',
    years: matchInt(value, /y(\d+)/, 0),
    levels: {
      // default base level is junior so a salary always shows
      base: [matchInt(value, /l(\d)/, 1)],
      management: management
        ? management.slice(1, 4).map((e) => parseInt(e))
        : [0, 0, 0],
      support: support ? support.slice(1, 3).map((e) => parseInt(e)) : [0, 0],
    },
  };
}

export function useHGF2(state: string): HGF2State {
  const [, setState] = useState(0);
  // Start from the default so the first (hydration) render matches the
  // server-rendered markup, which never has a URL hash. The actual hash is only
  // available in the browser, so we restore it after mount below — otherwise a
  // hydration mismatch leaves the controlled inputs stuck on their defaults.
  const stateRef = useRef(
    new HGF2State(parseHgf2('2j0l1y0m000s00'), () =>
      setState((value) => value + 1)
    )
  );
  useEffect(() => {
    stateRef.current.load(parseHgf2(state || '2j0l1y0m000s00'));
  }, [state]);
  return stateRef.current;
}

// --- Track data (from HGF 2.0.csv) ---------------------------------------

export const baseCategories: Record<Discipline, Category> = {
  developer: {
    id: 'base',
    tracks: [
      {
        id: 'core',
        name: 'Core Delivery',
        levels: [
          {
            level: 'junior',
            examples: [
              'Scope: Focuses on improving their knowledge of tools and processes while working on defined tasks within a project under guidance of others.',
              'Builds smaller features independently or bigger features with the help of others.',
              'Actively looks for learning opportunities.',
              'Communicates clearly with peers and asks clarifying questions.',
              'Incorporates feedback effectively and iterates quickly.',
              'Participates actively in team rituals and code reviews.',
            ],
          },
          {
            level: 'mid',
            examples: [
              'Scope: Owns end-to-end implementation of features and smaller projects and fully understands the languages, frameworks and toolings of the stacks they work in.',
              'Owns and coordinates development of small to medium-sized features.',
              'Has a deep understanding of all the codebases they work on.',
              'Teaches others through code reviews, pairing, and documentation.',
              'Proposes and implements small measures to improve code quality and developer experience/maintainability.',
              'Follows established best practices for documentation and pull request reviews.',
            ],
          },
          {
            level: 'senior',
            examples: [
              'Scope: Leads development of complex features and systems across teams. Understands not just the how but the why — from framework internals to long-term product impact.',
              'Owns, coordinates, and leads development of larger features, involving the right people at the right time.',
              'Proactively improves code quality and developer experience beyond immediate task requirements.',
              'Identifies edge cases and potential issues early in the implementation process.',
              'Mentors and supports colleagues through code reviews, pairing, and knowledge sharing.',
              'Collaborates with designers and PMs to anticipate technical risks and ensure smooth delivery.',
            ],
          },
          {
            level: 'principal',
            examples: [
              'Scope: Operates at the highest level of development mastery. Trusted expert that is involved in creating and maintaining the foundations of what we work on in strong collaboration with product and tech leads.',
              'Solves the hardest technical problems across projects — the ones others get stuck on.',
              'Debugs and resolves issues that require deep understanding of framework internals.',
              'Tracks where our tools are heading and keeps the team informed about upcoming changes and risks.',
              'Actively contributes to our open source dependencies.',
              'Evaluates, selects, and when necessary builds frameworks and abstractions; prefers proven solutions over homegrown ones.',
              'Advises on technical bets and long-term technology decisions.',
            ],
          },
        ],
      },
    ],
  },
  designer: {
    id: 'base',
    tracks: [
      {
        id: 'core',
        name: 'Core Delivery',
        levels: [
          {
            level: 'junior',
            examples: [
              'Scope: Focuses on mastering tools, processes, and collaboration basics. Works on defined tasks within a project under guidance.',
              'Produces high-quality visuals, layouts, or flows for assigned features.',
              'Incorporates feedback effectively and iterates quickly.',
              'Applies the basics of accessibility and design systems.',
              'Communicates clearly with peers and asks clarifying questions.',
              'Participates actively in critiques and team rituals.',
            ],
          },
          {
            level: 'mid',
            examples: [
              'Scope: Owns end-to-end design for features or small projects. Collaborates directly with developers and product managers.',
              'Translates research and briefs into clear user flows, wireframes, and UI designs.',
              'Makes thoughtful trade-offs between quality, speed, and feasibility.',
              'Collaborates closely with developers on implementation details.',
              'Runs lightweight usability checks or design reviews.',
              'Contributes to shared design systems or pattern libraries.',
            ],
          },
          {
            level: 'senior',
            examples: [
              'Scope: Leads design work for complex products or major clients. Shapes user experience and visual identity at the project level.',
              'Drives discovery workshops and aligns design direction with client or product strategy.',
              'Mentors junior designers and provides structured feedback.',
              'Balances business goals, user needs, and technical feasibility with confidence.',
              'Evolves design systems and component libraries.',
              'Advocates for user-centric and accessible design decisions in cross-disciplinary discussions.',
              'Collaborates proactively with developers and PMs to anticipate risks and define smooth handoffs.',
            ],
          },
          {
            level: 'principal',
            examples: [
              'Scope: Operates at the highest level of design mastery. Trusted expert in solving complex design challenges, mentoring others by example, and partnering with Product and Tech Leads to ensure design excellence.',
              'Solves wicked problems — the hardest design challenges across products or clients.',
              'Brings new tools, workflows, and creative methods to the studio.',
              'Acts as a mentor and thought leader within projects, not as a formal manager.',
              'Contributes to design vision through practice, not through ownership of systems or people.',
              'Represents design excellence in a professional context (case studies, design community events).',
            ],
          },
        ],
      },
    ],
  },
  product: {
    id: 'base',
    tracks: [
      {
        id: 'core',
        name: 'Core Delivery',
        levels: [
          {
            level: 'junior',
            examples: [
              "Scope: Focuses on learning client process, tools, and the studio's delivery rhythm. Supports senior PMs on one or more projects.",
              'Manages backlog hygiene and sprint boards.',
              'Supports requirements gathering and documentation.',
              'Tracks product metrics and assists with reporting.',
              'Coordinates with design and dev to clarify tickets.',
              'Participates in client meetings and note-taking.',
            ],
          },
          {
            level: 'mid',
            examples: [
              'Scope: Owns small portfolio end-to-end. Balances user, business, and technical priorities independently. Manages day-to-day client communication and alignment.',
              'Defines and maintains a clear product roadmap in collaboration with the client.',
              'Prioritizes backlog based on value and feasibility.',
              'Facilitates sprint planning, reviews, and retros.',
              'Tracks KPIs and reports outcomes to clients.',
              'Translates high-level strategy into delivery goals for design and dev teams.',
            ],
          },
          {
            level: 'senior',
            examples: [
              'Scope: Leads multiple client products or a complex multi-stakeholder engagement. Connects discovery, design, and delivery to drive measurable outcomes.',
              'Shapes product vision and strategic roadmaps across projects.',
              'Facilitates discovery workshops and decision frameworks with clients.',
              'Coaches other PMs or hybrid leads (designers/devs handling PM work).',
              'Identifies cross-product insights and reusable practices.',
              'Partners with Technical and Design Leads to balance product ambition with feasibility.',
              'Anticipates client challenges and proposes proactive solutions.',
            ],
          },
          {
            level: 'principal',
            examples: [
              'Scope: Defines how the studio approaches product strategy, discovery, and outcome-driven delivery. Leads product excellence across multiple teams and accounts.',
              "Shapes the studio's product frameworks, rituals, and playbooks.",
              'Partners with leadership on how to integrate product thinking into every engagement.',
              'Coaches Senior PMs and cross-discipline leads.',
              'Facilitates alignment between delivery teams and business strategy.',
              "Represents the studio's product philosophy externally (talks, workshops, content).",
              'Identifies opportunities for long-term client partnerships or new product ventures.',
            ],
          },
        ],
      },
    ],
  },
};

export const managementCategory: Category = {
  id: 'management',
  tracks: [
    {
      id: 'project',
      name: 'Project / Customer Management',
      levels: [
        {
          level: 'L1',
          examples: [
            'Supports customers and internal initiatives as first point of contact.',
            'Assists in sprint planning, retros, and standups.',
            'Documents decisions and maintains project boards.',
            'Escalates blockers early.',
            'Answers simple customer questions.',
            'Reproduces customer bug reports and turns them into actionable tickets.',
            'Keeps tabs on the current sprint progress and facilitates the completion of the sprint.',
            'Joins and documents customer meetings.',
          ],
        },
        {
          level: 'L2',
          examples: [
            'Owns delivery for at least one client.',
            'Balances project scope and deadlines.',
            'Keeps client expectations aligned with team reality.',
            'Anticipates risks and works proactively to mitigate them.',
          ],
        },
      ],
    },
    {
      id: 'people',
      name: 'People Management',
      levels: [
        {
          level: 'L1',
          examples: [
            'Leads a small group (3–5 people) while remaining hands-on in their craft.',
            'Runs regular 1:1s focused on growth and wellbeing.',
            'Gives feedback and helps resolve team conflicts.',
            'Coordinates with other leads on delivery and priorities.',
            'Conducts hiring screening meetings as needed.',
          ],
        },
        {
          level: 'L2',
          examples: [
            'Owns leadership and decision-making for their team. Has final authority on hiring, trial periods, and contract terminations.',
            'Conducts yearly review of job levels and helps team members take the next step.',
            'Coaches individuals through skill progression.',
            'Partners with other managers to align goals.',
            'Drives hiring, onboarding, and retention for their team.',
          ],
        },
      ],
    },
    {
      id: 'craft',
      name: 'Craft Management',
      levels: [
        {
          level: 'L1',
          examples: [
            'Leads technical/design direction on a single project.',
            'Defines architecture or design systems and keeps the project on track.',
            'Starts and coordinates bigger initiatives to improve projects, from major refactorings to new technologies.',
            'Conducts technical hiring interviews.',
            'Coordinates cross-functional implementation.',
            'Provides technical feedback.',
            'Ensures delivery quality.',
          ],
        },
        {
          level: 'L2',
          examples: [
            'Guides multiple projects or complex systems.',
            'Key difference to Principal roles: focused on cross-team responsibility and standardization rather than individual mastery.',
            'Shapes technical/design practices across teams.',
            'Standardizes frameworks or patterns.',
            'Advises PMs and clients on technical feasibility and scalability.',
            'Leads initiatives to modernize codebases, increase DX and improve workflows.',
          ],
        },
      ],
    },
  ],
};

export const supportCategory: Category = {
  id: 'support',
  tracks: [
    {
      id: 'evangelism',
      name: 'Evangelism',
      levels: [
        {
          level: 'L1',
          examples: [
            'Represents nerdgeschoss at local events and creates marketing material.',
            'Regularly represents nerdgeschoss at meetups with contributions or lightning talks.',
            'Creates simple case studies and material for marketing efforts.',
            'Engages with the local tech community on behalf of the studio.',
          ],
        },
        {
          level: 'L2',
          examples: [
            'Represents nerdgeschoss at conferences and produces content with lasting marketing value.',
            'Delivers fully researched talks at relevant conferences.',
            'Turns talk material into blog posts and reusable marketing content.',
            'Builds a recognizable presence in the broader tech community.',
            'Mentors L1 colleagues in preparing their first talks.',
          ],
        },
      ],
    },
    {
      id: 'opensource',
      name: 'Open Source',
      levels: [
        {
          level: 'L1',
          examples: [
            'Contributes to and maintains smaller open source projects relevant to the company.',
            'Owns a small internal library or tool.',
            'Reviews contributions from peers and external developers.',
            'Documents processes, APIs, and examples.',
            'Advocates for open source within the studio.',
            'Ensures contributions are well-tested and maintainable.',
          ],
        },
        {
          level: 'L2',
          examples: [
            'Drives open source strategy and maintains significant external projects.',
            'Maintains one or more externally visible open source projects.',
            'Reviews and guides external contributions.',
            'Represents nerdgeschoss in open source communities.',
            "Identifies strategic open source opportunities that benefit the studio's work.",
          ],
        },
      ],
    },
  ],
};
